import { createStart, createMiddleware } from "@tanstack/react-start";
import { attachSupabaseAuthLazy } from "@/lib/supabase-auth-attacher-lazy";

// Module-init timestamp. On a warm worker this stays constant across requests,
// so (now - MODULE_INIT_AT) tells us how long this worker instance has been alive.
// First request after a cold start will show a small number; subsequent requests
// will show ever-increasing values — useful to confirm warm vs cold.
const MODULE_INIT_AT = Date.now();

/**
 * Diagnostic middleware: emits a Server-Timing header so we can attribute
 * the observed TTFB to (a) worker plumbing / module init or (b) the actual
 * request handler (router + React SSR + response serialization).
 *
 * Read with:
 *   curl -sI https://calvarius.com | grep -i server-timing
 *
 *   handler;dur=<ms>   total time spent inside next() — router + SSR + flush
 *   warm;dur=<ms>      ms since this worker instance booted (cold ≈ 0)
 */
const serverTiming = createMiddleware().server(async ({ next }) => {
  const t0 = performance.now();
  const result = await next();
  const handlerMs = performance.now() - t0;
  const warmMs = Date.now() - MODULE_INIT_AT;

  const timing = `handler;dur=${handlerMs.toFixed(1)}, warm;dur=${warmMs}`;

  // Attach Server-Timing to the outgoing response if we can reach it.
  const maybeResponse = (result as { response?: Response } | undefined)?.response;
  if (maybeResponse && maybeResponse.headers && !maybeResponse.headers.has("server-timing")) {
    try {
      maybeResponse.headers.set("server-timing", timing);
    } catch {
      // Some response objects have immutable headers — ignore silently.
    }
  }

  return result;
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuthLazy],
  requestMiddleware: [serverTiming],
}));
